import { useEffect, useMemo, useState } from "react";
import CarouselRail from "@/components/yts/CarouselRail";
import ExperienceCard from "@/components/yts/ExperienceCard";
import { useExplore, toCard } from "@/lib/ExploreContext";
import { useLang } from "@/lib/i18n";
import { base44 } from "@/api/base44Client";
import { normalizeViator } from "@/lib/viator";
import { withNetworkRetry } from "@/lib/requestRetry";

// Hand-reviewed records in the current Viator catalog. Other similarly named
// records had unrelated booking-page slugs and aren't promoted here.
const CURATED_IDS = [
  "6ab9682d8d9c5bfdc07ee258", // Carabalí / Luquillo Beach
  "6ab9686849931c3a648fb20f", // Secluded beaches and tropical forest
  "6ab9686849931c3a648fb1f0", // San Juan horseback tour
  "6ab9686aaad6f4af94d299e9", // Aguadilla Beach
  "6ab9686aaad6f4af94d299f3", // Vieques / Isla Nena
  "6ab9686aaad6f4af94d29a2b", // Scenic ride from San Juan
  "6ab96869c558ac91c411187e", // Isabela beach
  "6ab96869c558ac91c41118a8", // Afternoon Isabela ride
];
const rank = new Map(CURATED_IDS.map((id, index) => [id, index]));

const cleanHorse = (item) => Boolean(
  item?.id && rank.has(item.id)
  && /horseback|horse ride|horse riding/i.test(item.title || item.name || "")
  && item.booking_url?.includes("viator.com/tours/")
  && (item.cover_image || item.image || item.images?.[0])
);

export default function HorsebackHomeRail() {
  const { viatorListings, nbNames } = useExplore();
  const { lang } = useLang();
  const es = lang === "es";
  const [loaded, setLoaded] = useState([]);

  useEffect(() => {
    let alive = true;
    // The normal homepage cache holds only the top ~200 tours. Fetch these
    // specific tours independently so the horse rail is never just one card.
    withNetworkRetry(() => base44.entities.ViatorExperience.filter(
      { status: "Published", title: { $regex: "horseback|horse ride|horse riding", $options: "i" } },
      { sort: "-review_count", limit: 40 }
    )).then((result) => {
      if (!alive) return;
      const rows = Array.isArray(result) ? result : (result?.items || []);
      setLoaded(rows.map(normalizeViator));
    }).catch(() => {
      // A network failure retains eligible cards already in the page cache.
    });
    return () => { alive = false; };
  }, []);

  const items = useMemo(() => {
    const unique = new Map([...viatorListings, ...loaded].filter(cleanHorse).map((item) => [item.id, item]));
    return [...unique.values()].sort((a, b) => rank.get(a.id) - rank.get(b.id))
      .map((item) => ({ ...toCard(item, nbNames), label: es ? "Paseos a caballo" : "Horseback riding" }));
  }, [viatorListings, loaded, nbNames, es]);

  if (!items.length) return null;
  return (
    <div id="home-horseback" className="scroll-mt-[120px]">
      <CarouselRail
        masthead={es ? "PLAYA + CAMPO" : "BEACH + COUNTRYSIDE"}
        title={es ? "A CABALLO." : "HORSEBACK RIDES."}
        subtitle={es ? "Arena, caminos y bosques tropicales. Paseos reales para reservar a través de Viator." : "Sandy shores, countryside trails and tropical forests. Real horseback rides bookable through Viator."}
        itemWidth="w-[min(67vw,260px)] min-w-[200px] sm:w-[250px] sm:min-w-0"
      >
        {items.map((item) => <ExperienceCard key={item.id} item={item}/>)}
      </CarouselRail>
    </div>
  );
}